import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChildren
} from "@angular/core";
import {Subscription} from "rxjs";
import {PossessivePipe} from "../../string-utils/possessive.pipe";
import {NavigationService} from "../../orchestration/navigation.service";
import {MessageLevel} from "../../notification/message/message";
import {OrderModel, TransferRequest} from "../order.model";
import {OrderingContextModel} from "../../ordering-context/ordering-context.model";
import {OrchestrationEvents} from "../../orchestration/orchestration-events";
import {GroupOrderEvents} from "../../group-order/group-order-events";
import {OrderEvents, OrderInitiationStatus} from "../order-events";
import {NotificationService} from "../../notification/notification.service";
import {AnalyticsService} from "../../orchestration/analytics.service";
import {MenuModel} from "../../menu/menu.model";
import {OrderTransferTargetStatus} from "../../group-order/order-transfer-request-poll.service";

import {DisplayableOrderItem, OrderType} from "../order";

@Component({
    selector: "woa-order-edit-panel",
    templateUrl: "order-edit-panel.component.html"
})

export class OrderEditPanelComponent implements AfterViewInit, OnDestroy {
    @Input() showRetry: boolean = true;
    @Input() showItemEdit: boolean = true;
    @Input() disabled: boolean = false;
    @Input() collapseTransferRequests: boolean = true;
    @Output() onRetry: EventEmitter<void> = new EventEmitter<void>();
    @Output() onEditOrderItem: EventEmitter<DisplayableOrderItem> = new EventEmitter();
    @Output() onRemoveOrderItem: EventEmitter<DisplayableOrderItem> = new EventEmitter();

    @ViewChildren("requestItems", {read: ElementRef}) protected requestItems: QueryList<ElementRef>;

    OrderType = OrderType;
    OrderInitiationStatus = OrderInitiationStatus;
    MessageLevel = MessageLevel;
    OrderTransferTargetStatus = OrderTransferTargetStatus;
    OrchestrationEvents = OrchestrationEvents;
    GroupOrderEvents = GroupOrderEvents;
    OrderEvents = OrderEvents;

    transferRequestExpanded: { [transferRequestId: string]: boolean } = {};
    transferRequestHeights: { [transferRequestId: string]: number } = {};

    private subscriptions: Subscription = new Subscription();

    constructor(public orderingContextModel: OrderingContextModel,
                public orderModel: OrderModel,
                public menuModel: MenuModel,
                private navigationService: NavigationService,
                private changeDetectorRef: ChangeDetectorRef,
                private notificationService: NotificationService,
                private analyticsService: AnalyticsService) {
        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => this.changeDetectorRef.detectChanges()));
    }

    ngAfterViewInit(): void {
        this.setTransferRequestHeights();
        this.subscriptions.add(this.requestItems.changes.subscribe(() => this.setTransferRequestHeights()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get showRequestHeadings(): boolean {
        return !!this.orderModel.totalTransfers || GroupOrderEvents.groupOrderStatus$.value === OrderTransferTargetStatus.OPEN;
    }

    isCompositeItem(orderItem: DisplayableOrderItem): boolean {
        return orderItem.compositeOrderItems && orderItem.compositeOrderItems.length > 0;
    }

    editOrderItem(orderItem: DisplayableOrderItem): void {
        this.onEditOrderItem.emit(orderItem);
    }

    removeOrderItem(orderItem: DisplayableOrderItem): void {
        this.onRemoveOrderItem.emit(orderItem);
    }

    public isTransferRequestExpanded(request: TransferRequest): boolean {
        if (this.transferRequestExpanded[request.transferRequestId] === undefined) {
            this.transferRequestExpanded[request.transferRequestId] = !request.transferRequestId || !this.collapseTransferRequests;
        }
        return this.transferRequestExpanded[request.transferRequestId];
    }

    public toggleTransferRequest(request: TransferRequest): void {
        this.transferRequestExpanded[request.transferRequestId] = !this.isTransferRequestExpanded(request);
        this.setTransferRequestHeight(request);
    }

    private setTransferRequestHeights(): void {
        this.orderModel.transferRequests.forEach((request: TransferRequest) => this.setTransferRequestHeight(request));
    }

    public setTransferRequestHeight(request: TransferRequest): void {
        if (!this.showRequestHeadings || !this.requestItems) {
            return;
        }
        const requestIndex: number = this.orderModel.transferRequests.indexOf(request);
        const items: HTMLElement = this.requestItems.toArray()[requestIndex].nativeElement;
        setTimeout(() => {
            this.transferRequestHeights[request.transferRequestId] = this.isTransferRequestExpanded(request) ? items.scrollHeight : 0;
            this.changeDetectorRef.detectChanges();
            if (this.isTransferRequestExpanded(request)) {
                setTimeout(() => items.scrollIntoView({
                    block: "end",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
        });
    }

    public getTransferRequestName(request: TransferRequest) {
        return request.transferRequestId ? new PossessivePipe().transform(request.transferRequestName) : undefined;
    }

    public getTransferRequestHeading(request: TransferRequest) {
        return request.transferRequestId ? "woa.heading.order.summary.items.transferred" : "woa.heading.order.summary.items.local";
    }

    public async quantityChanged(orderItem: DisplayableOrderItem): Promise<void> {
        return this.analyticsService.trackItemQtyChanged(orderItem);
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order-edit-panel/order-edit-panel.component.ts